/**
 * Variant Selection Modal
 * Opens when user clicks on a product card
 * Shows large variant icons and allows selection + add to cart
 */

class VariantSelectionModal {
  constructor() {
    console.log('[Variant Modal] Initializing...');
    this.modal = document.getElementById('variant-selection-modal');
    if (!this.modal) {
      console.error('[Variant Modal] Modal element not found!');
      return;
    }

    console.log('[Variant Modal] Modal element found:', this.modal);

    this.overlay = this.modal.querySelector('.variant-modal-overlay');
    this.closeBtn = this.modal.querySelector('.variant-modal-close');
    this.optionsContainer = this.modal.querySelector('.variant-modal-options');
    this.addBtn = this.modal.querySelector('.variant-modal-add-btn');
    this.successEl = this.modal.querySelector('.variant-modal-success');
    this.selectedInfo = this.modal.querySelector('.variant-modal-selected-info');

    this.currentProduct = null;
    this.selectedVariant = null;
    this.productHandle = null; // Store current product handle
    this.previousUrl = null; // Store the URL before opening modal
    this.isClosing = false; // Prevent double-close from history nav

    console.log('[Variant Modal] Initialized successfully');
    this.init();
  }

  init() {
    // Close modal events
    this.overlay.addEventListener('click', () => this.close());
    this.closeBtn.addEventListener('click', () => this.close());

    // Add to cart event
    this.addBtn.addEventListener('click', () => this.addToCart());

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.close();
      }
    });

    // Handle browser back/forward buttons
    window.addEventListener('popstate', (e) => this.handlePopState(e));

    // Listen for product card clicks
    this.attachProductCardListeners();
  }

  attachProductCardListeners() {
    // Listen for clicks on product names and product images
    document.addEventListener('click', (e) => {
      // Check if clicked on product name (heading)
      const productName = e.target.closest('.product__name');
      // Check if clicked on product image area
      const productImage = e.target.closest('.product__inner');

      const clickedElement = productName || productImage;
      if (!clickedElement) {
        return;
      }

      const productItem = clickedElement.closest('.product-item');
      if (!productItem) {
        return;
      }

      // Check if product has multiple variants
      const hasVariants = productItem.dataset.hasVariants === 'true';
      if (!hasVariants) {
        // No variants, allow normal navigation
        return;
      }

      // Product has variants - prevent navigation and open modal
      e.preventDefault();
      e.stopPropagation();

      console.log('[Variant Modal] Opening modal for product:', productItem.dataset.productId);
      this.openForProduct(productItem);
    });
  }

  async openForProduct(productItem) {
    const productId = productItem.dataset.productId;

    // Get product data
    const productTitle = productItem.querySelector('.product__name')?.textContent.trim();
    const productImage = productItem.querySelector('.product__img')?.src;
    const productPrice = productItem.querySelector('.product__price')?.textContent.trim();

    // Extract product handle from URL
    const productLink = productItem.querySelector('a')?.href;
    const handleMatch = productLink?.split('/products/')[1]?.split('?')[0];

    if (!handleMatch) {
      console.error('[Variant Modal] Could not extract product handle');
      return;
    }

    this.productHandle = handleMatch;

    // Fetch product data from Shopify API
    try {
      const response = await fetch(`/products/${this.productHandle}.js`);
      const product = await response.json();

      this.currentProduct = product;
      this.populateModal(product, productTitle, productImage, productPrice);
      this.open();
    } catch (error) {
      console.error('Error fetching product:', error);
    }
  }

  populateModal(product, title, image, price) {
    // Set product info
    this.modal.querySelector('.variant-modal-product-title').textContent = title || product.title;

    // Use high-quality product image from Shopify
    const highQualityImage = product.featured_image
      ? product.featured_image.replace(/\.(jpg|jpeg|png|gif|webp)/, '_400x.$1')
      : image;
    this.modal.querySelector('.variant-modal-product-image').src = highQualityImage;

    // Set floating pet images to the product image
    const floatingPets = this.modal.querySelectorAll('.variant-modal-floating-pet img');
    floatingPets.forEach(petImg => {
      petImg.src = highQualityImage;
    });

    // Format price with sale styling
    this.modal.querySelector('.variant-modal-product-price').innerHTML = this.formatProductPrice(product);

    // Clear previous options
    this.optionsContainer.innerHTML = '';

    // Get variant icons from theme settings
    const normalIcon = document.documentElement.style.getPropertyValue('--variant-normal-icon');
    const neonIcon = document.documentElement.style.getPropertyValue('--variant-neon-icon');
    const megaIcon = document.documentElement.style.getPropertyValue('--variant-mega-icon');

    // Create variant options (limit to first 3)
    product.variants.slice(0, 3).forEach(variant => {
      const variantName = variant.title.toLowerCase();
      let iconSrc = '';

      // Determine icon based on variant name
      if (variantName.includes('normal')) {
        iconSrc = normalIcon || window.Shopify?.theme?.settings?.variant_normal_icon;
      } else if (variantName.includes('neon')) {
        iconSrc = neonIcon || window.Shopify?.theme?.settings?.variant_neon_icon;
      } else if (variantName.includes('mega')) {
        iconSrc = megaIcon || window.Shopify?.theme?.settings?.variant_mega_icon;
      }

      const option = document.createElement('div');
      option.className = `variant-modal-option ${!variant.available ? 'unavailable' : ''}`;
      option.dataset.variantId = variant.id;
      option.dataset.variantPrice = variant.price;
      option.dataset.variantComparePrice = variant.compare_at_price || variant.price;
      option.dataset.variantTitle = variant.title;

      option.innerHTML = `
        ${iconSrc ? `<img src="${iconSrc}" alt="${variant.title}" class="variant-modal-option-icon" />` : `<div class="variant-modal-option-label">${variant.title}</div>`}
        ${!iconSrc ? '' : `<div class="variant-modal-option-label">${variant.title}</div>`}
        <div class="variant-modal-option-price">${this.formatVariantOptionPrice(variant)}</div>
        ${!variant.available ? '<span class="variant-modal-option-sold-out">Sold Out</span>' : ''}
      `;

      if (variant.available) {
        option.addEventListener('click', () => this.selectVariant(variant, option));
      }

      this.optionsContainer.appendChild(option);
    });

    // Reset selected variant
    this.selectedVariant = null;
    this.updateAddButton();
  }

  selectVariant(variant, optionEl) {
    // Remove active class from all options
    this.optionsContainer.querySelectorAll('.variant-modal-option').forEach(opt => {
      opt.classList.remove('active');
    });

    // Add active class to selected
    optionEl.classList.add('active');

    // Store selected variant
    this.selectedVariant = variant;

    // Update main product price in header (below image)
    this.modal.querySelector('.variant-modal-product-price').innerHTML = this.formatVariantPrice(variant);

    // Enable add button
    this.updateAddButton();
  }

  updateAddButton() {
    if (this.selectedVariant) {
      this.addBtn.disabled = false;
      this.addBtn.querySelector('span').textContent = 'Add to Cart';
    } else {
      this.addBtn.disabled = true;
      this.addBtn.querySelector('span').textContent = 'Select a variant';
    }
  }

  async addToCart() {
    if (!this.selectedVariant) return;

    this.addBtn.disabled = true;
    this.addBtn.querySelector('span').textContent = 'Adding...';

    try {
      console.log('[Variant Modal] Adding to cart:', {
        variantId: this.selectedVariant.id
      });

      // Step 1: Add item to cart using JSON endpoint
      const formData = new FormData();
      formData.append('id', this.selectedVariant.id);
      formData.append('quantity', 1);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      console.log('[Variant Modal] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Variant Modal] Add to cart failed:', errorText);
        throw new Error(`Failed to add to cart: ${response.status}`);
      }

      const cartItem = await response.json();
      console.log('[Variant Modal] Item added to cart:', cartItem);

      this.showSuccess();

      // Step 2: Refresh the cart drawer by fetching sections
      await this.refreshCartDrawer();

      // Step 3: Update cart count
      this.updateCartCount();

      // Close modal after delay
      setTimeout(() => {
        this.close();
      }, 2000);
    } catch (error) {
      console.error('[Variant Modal] Error adding to cart:', error);
      this.addBtn.disabled = false;
      this.addBtn.querySelector('span').textContent = 'Try Again';

      // Show more specific error message
      const errorMsg = error.message || 'Failed to add to cart. Please try again.';
      alert(errorMsg);
    }
  }

  async refreshCartDrawer() {
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        console.log('[Variant Modal] Cart drawer not found, skipping refresh');
        return;
      }

      // Get the sections the cart drawer needs
      const sections = cartDrawer.getSectionsToRender
        ? cartDrawer.getSectionsToRender().map(section => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      console.log('[Variant Modal] Fetching sections:', sections);

      // Fetch the sections HTML
      const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
      const sectionsData = await response.json();

      console.log('[Variant Modal] Sections data received');

      // Build the format that renderContents expects
      const cartData = {
        sections: sectionsData
      };

      // Use the cart drawer's built-in method
      if (cartDrawer.renderContents && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(cartData);
        console.log('[Variant Modal] Cart drawer refreshed successfully');
      } else {
        console.log('[Variant Modal] renderContents method not found, using fallback');
        // Fallback: dispatch event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: cartData,
          bubbles: true
        }));
      }
    } catch (error) {
      console.error('[Variant Modal] Error refreshing cart drawer:', error);
    }
  }

  showSuccess() {
    this.successEl.style.display = 'flex';
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartCount = cart.item_count;

      console.log('[Variant Modal] Updating cart count to:', cartCount);

      // Update cart count elements - try multiple selectors
      const selectors = [
        '.cart-count',
        '[data-cart-count]',
        '#cart-icon-bubble',
        '.cart-count-bubble',
        '[data-header-cart-count]'
      ];

      selectors.forEach(selector => {
        document.querySelectorAll(selector).forEach(el => {
          el.textContent = cartCount;
          if (cartCount > 0) {
            el.style.display = '';
            el.classList.remove('hidden');
          } else {
            el.classList.add('hidden');
          }
        });
      });

      // Dispatch multiple cart update events for theme compatibility
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: cart,
        bubbles: true
      }));

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: cart,
        bubbles: true
      }));

      // Try to update cart drawer if it exists
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
      }

      console.log('[Variant Modal] Cart count updated successfully');
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  formatProductPrice(product) {
    // Get the first variant's price info
    const variant = product.variants[0];
    return this.formatVariantPrice(variant);
  }

  formatVariantPrice(variant) {
    const price = variant.price;
    const comparePrice = variant.compare_at_price;

    // If there's a sale (compare_at_price > price)
    if (comparePrice && comparePrice > price) {
      return `
        <span class="sale-price" style="color: #e84e9a; font-weight: 700; font-size: 1.1em;">
          ${this.formatMoney(price)}
        </span>
        <span class="regular-price" style="text-decoration: line-through; color: #999; margin-left: 8px; font-size: 0.95em;">
          ${this.formatMoney(comparePrice)}
        </span>
      `;
    }

    // Regular price (no sale)
    return `<span class="regular-price" style="font-weight: 600;">${this.formatMoney(price)}</span>`;
  }

  formatVariantOptionPrice(variant) {
    const price = variant.price;
    const comparePrice = variant.compare_at_price;

    // For variant selectors, only show the sale price (no strikethrough)
    if (comparePrice && comparePrice > price) {
      return `<span class="sale-price" style="color: #e84e9a; font-weight: 700;">${this.formatMoney(price)}</span>`;
    }

    // Regular price (no sale)
    return `<span class="regular-price" style="font-weight: 600;">${this.formatMoney(price)}</span>`;
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  handlePopState(event) {
    console.log('[Variant Modal] Popstate event:', event.state);

    // If modal is open and we navigated back, close it
    if (this.modal.classList.contains('active')) {
      console.log('[Variant Modal] Back button pressed, closing modal');
      this.close(true); // Pass true to indicate this is from popstate
    }
  }

  open() {
    // Reset floating pets exit animations
    const floatingPets = this.modal.querySelectorAll('.variant-modal-floating-pet');
    floatingPets.forEach(pet => {
      pet.classList.remove('exit-left', 'exit-right');
    });

    // Update URL to product page
    if (this.productHandle) {
      const productUrl = `/products/${this.productHandle}`;
      const currentUrl = window.location.pathname;

      // Store the previous URL so we can restore it on close
      this.previousUrl = currentUrl;

      // Only push state if we're not already on this product URL
      if (currentUrl !== productUrl) {
        console.log('[Variant Modal] Updating URL to:', productUrl);
        window.history.pushState(
          {
            modalOpen: true,
            productHandle: this.productHandle,
            previousUrl: currentUrl
          },
          '',
          productUrl
        );
      }
    }

    // Inject product schema markup for SEO
    this.injectProductSchema();

    this.modal.style.display = 'flex';
    setTimeout(() => {
      this.modal.classList.add('active');
    }, 10);
    document.body.style.overflow = 'hidden';
  }

  close(fromPopState = false) {
    // Prevent double-close
    if (this.isClosing) {
      return;
    }
    this.isClosing = true;

    console.log('[Variant Modal] Closing modal, fromPopState:', fromPopState);

    // Trigger exit animations for floating pets
    const floatingPets = this.modal.querySelectorAll('.variant-modal-floating-pet');
    floatingPets.forEach(pet => {
      const position = pet.dataset.position;
      if (position === 'left') {
        pet.classList.add('exit-left');
      } else if (position === 'right') {
        pet.classList.add('exit-right');
      }
    });

    // Remove product schema markup
    this.removeProductSchema();

    // Close the modal immediately for smooth UX
    this.modal.classList.remove('active');

    // If closing from user action (not back button), navigate back in history
    // The modal is already closing, so this just cleans up the URL
    if (!fromPopState && this.previousUrl) {
      console.log('[Variant Modal] Navigating back in history');
      // Small delay to let the close animation start
      setTimeout(() => {
        window.history.back();
      }, 50);
    }

    setTimeout(() => {
      this.modal.style.display = 'none';
      this.successEl.style.display = 'none';
      this.selectedInfo.style.display = 'none';
      document.body.style.overflow = '';
      this.isClosing = false;
      this.previousUrl = null;
    }, 300);
  }

  injectProductSchema() {
    if (!this.currentProduct) {
      console.log('[Variant Modal] No product data available for schema');
      return;
    }

    // Remove any existing modal schema first
    this.removeProductSchema();

    const product = this.currentProduct;

    // Build product URL
    const productUrl = `${window.location.origin}/products/${this.productHandle}`;

    // Build image URLs (convert to full URLs if needed)
    const images = product.images || [];
    const imageUrls = images.map(img => {
      // If image is already a full URL, use it; otherwise, prepend origin
      return img.startsWith('http') ? img : `${window.location.origin}${img}`;
    });

    // Build JSON-LD schema
    const schema = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.title,
      "url": productUrl,
      "image": imageUrls,
      "description": product.description || "",
      "brand": {
        "@type": "Brand",
        "name": product.vendor || "Unknown"
      },
      "offers": product.variants.map(variant => ({
        "@type": "Offer",
        "availability": variant.available
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        "price": (variant.price / 100).toFixed(2), // Convert cents to dollars
        "priceCurrency": "USD",
        "url": `${productUrl}?variant=${variant.id}`
      }))
    };

    // Create and inject script tag
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'modal-schema';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    console.log('[Variant Modal] Product schema injected:', schema);
  }

  removeProductSchema() {
    const existingSchema = document.getElementById('modal-schema');
    if (existingSchema) {
      existingSchema.remove();
      console.log('[Variant Modal] Product schema removed');
    }
  }
}

// Initialize modal when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantSelectionModal();
  });
} else {
  new VariantSelectionModal();
}
